import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { API_BASE } from '../../services/api';

interface Props {
 onClose: () => void;
 defaultTab?: 'login' | 'register';
 resetToken?: string | null;
 /**
 * Optional caller-supplied message shown above the form. Used to explain
 * why auth is being requested (e.g. when a logged-out user clicks Save).
 */
 message?: string | null;
}

export const AuthModal: React.FC<Props> = ({ onClose, defaultTab = 'login', resetToken = null, message = null }) => {
 const { login, register } = useAuth();
 const [tab, setTab] = useState<'login' | 'register' | 'forgot' | 'reset'>(resetToken ? 'reset' : defaultTab);
 const [email, setEmail] = useState('');
 const [password, setPassword] = useState('');
 const [confirmPassword, setConfirmPassword] = useState('');
 const [error, setError] = useState<string | null>(null);
 const [success, setSuccess] = useState<string | null>(null);
 const [loading, setLoading] = useState(false);

 const handleSubmit = async (e: React.FormEvent) => {
 e.preventDefault();
 setError(null);
 setSuccess(null);
 setLoading(true);
 try {
 if (tab === 'login') {
 await login(email, password);
 onClose();
 } else if (tab === 'register') {
 await register(email, password);
 onClose();
 } else if (tab === 'forgot') {
 const res = await fetch(`${API_BASE}/api/auth/forgot-password`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ email }),
 });
 const data = await res.json();
 if (!res.ok) throw new Error(data.error);
 setSuccess(data.message || 'If an account exists with that email, a reset link has been generated.');
 } else if (tab === 'reset') {
 if (password !== confirmPassword) throw new Error('Passwords do not match.');
 const res = await fetch(`${API_BASE}/api/auth/reset-password`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ token: resetToken, password }),
 });
 const data = await res.json();
 if (!res.ok) throw new Error(data.error);
 setSuccess('Password has been reset. You can now sign in.');
 setTimeout(() => { setTab('login'); setSuccess(null); }, 2000);
 }
 } catch (err: any) {
 setError(err.message);
 } finally {
 setLoading(false);
 }
 };

 const title = tab === 'login' ? 'Sign in'
 : tab === 'register' ? 'Create your account'
 : tab === 'forgot' ? 'Reset your password'
 : 'Set a new password';

 const standfirst = tab === 'login' ? 'Welcome back. Your saved cases are waiting.'
 : tab === 'register' ? 'One account keeps every estate review in one place.'
 : tab === 'forgot' ? 'Enter your email and we’ll send a secure reset link.'
 : 'Choose a new password of at least eight characters.';

 // Underline-style field — editorial register, no boxed input, no icon.
 const fieldClass = (kind: 'serif' | 'mono') =>
 `w-full py-2 px-0 border-0 border-b border-rule bg-transparent text-ink outline-none transition-colors focus:border-accent ${
 kind === 'mono' ? 'font-mono text-[15px]' : 'font-serif text-[18px]'
 }`;
 const labelClass = 'block text-[9px] tracking-[0.16em] uppercase text-ink3 font-semibold mb-2';

 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(26,24,20,0.28)] backdrop-blur-sm p-4">
 <div className="relative w-full max-w-[420px] bg-white border border-rule border-t-2 border-t-ink shadow-[0_40px_80px_rgba(20,20,20,0.22)]">
 {/* Masthead */}
 <div className="flex items-start justify-between px-8 pt-[22px] pb-5 border-b border-rule">
 <div>
 <div className="font-serif text-[17px] text-ink leading-none mb-1">
 Estate<span className="italic">clarity</span>
 </div>
 <div className="text-[9px] tracking-eyebrow uppercase text-ink3">
 Probate &amp; IHT review
 </div>
 </div>
 <button
 onClick={onClose}
 aria-label="Close"
 className="font-serif text-[20px] leading-none text-ink3 hover:text-ink transition-colors"
 >
 ×
 </button>
 </div>

 {/* Title block */}
 <div className="px-8 pt-[26px]">
 <h2 className="font-serif font-normal text-[28px] tracking-[-0.015em] leading-[1.1] text-ink m-0">
 {title}
 </h2>
 <p className="font-serif italic text-[13px] text-ink2 mt-2 leading-[1.5]">
 {standfirst}
 </p>
 </div>

 {/* Tabs — only show for login/register */}
 {(tab === 'login' || tab === 'register') && (
 <div className="flex gap-7 px-8 pt-5">
 {(['login', 'register'] as const).map(t => {
 const active = tab === t;
 return (
 <button
 key={t}
 onClick={() => { setTab(t); setError(null); setSuccess(null); }}
 className={`pb-2.5 -mb-px border-b transition-colors ${
 active
 ? 'font-serif italic text-[15px] text-ink border-ink'
 : 'font-sans font-medium text-[12px] text-ink3 border-transparent hover:text-ink'
 }`}
 >
 {t === 'login' ? 'Sign in' : 'Create account'}
 </button>
 );
 })}
 </div>
 )}

 {/* Form */}
 <form onSubmit={handleSubmit} className="px-8 pt-6 pb-2">
 {message && (tab === 'login' || tab === 'register') && (
 <div className="mb-5 bg-accent-soft border border-accent-rule px-3.5 py-3 font-serif italic text-[12px] text-accent leading-[1.5]">
 {message}
 </div>
 )}
 {error && (
 <div className="mb-5 flex items-start gap-2 border border-[#E8D89A] bg-[#FAF3E0] px-3.5 py-3 text-[12px] text-[#7A5A00] leading-[1.5]">
 <span className="font-serif italic leading-none">!</span>
 <span>{error}</span>
 </div>
 )}
 {success && (
 <div className="mb-5 flex items-start gap-2 border border-accent-rule bg-accent-soft px-3.5 py-3 text-[12px] text-accent leading-[1.5]">
 <span className="font-serif italic leading-none">✓</span>
 <span>{success}</span>
 </div>
 )}

 {/* Email — shown for login, register, forgot */}
 {(tab === 'login' || tab === 'register' || tab === 'forgot') && (
 <div className="mb-5">
 <label className={labelClass}>Email</label>
 <input
 type="email"
 required
 autoFocus
 value={email}
 onChange={e => setEmail(e.target.value)}
 className={fieldClass('serif')}
 placeholder="you@example.com"
 />
 </div>
 )}

 {/* Password — shown for login, register, reset */}
 {(tab === 'login' || tab === 'register' || tab === 'reset') && (
 <div className="mb-5">
 <label className={labelClass}>
 {tab === 'reset' ? 'New password' : 'Password'}
 </label>
 <input
 type="password"
 required
 minLength={8}
 value={password}
 onChange={e => setPassword(e.target.value)}
 className={fieldClass('mono')}
 placeholder={tab === 'register' || tab === 'reset' ? 'At least 8 characters' : '••••••••'}
 />
 </div>
 )}

 {/* Confirm password — reset only */}
 {tab === 'reset' && (
 <div className="mb-5">
 <label className={labelClass}>Confirm password</label>
 <input
 type="password"
 required
 minLength={8}
 value={confirmPassword}
 onChange={e => setConfirmPassword(e.target.value)}
 className={fieldClass('mono')}
 placeholder="Re-enter password"
 />
 </div>
 )}

 {/* Forgot password link — login tab only, inline-right */}
 {tab === 'login' && (
 <div className="text-right -mt-1.5 mb-[18px]">
 <button
 type="button"
 onClick={() => { setTab('forgot'); setError(null); setSuccess(null); }}
 className="text-[11px] text-accent hover:text-accent-hover transition-colors"
 >
 Forgot your password?
 </button>
 </div>
 )}

 <button
 type="submit"
 disabled={loading}
 className={`w-full flex items-center justify-center gap-2.5 bg-accent hover:bg-accent-hover text-white text-[13px] font-medium tracking-[0.02em] py-[13px] px-5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
 tab === 'login' ? 'mt-0' : 'mt-5'
 }`}
 >
 {loading ? 'Please wait…'
 : tab === 'login' ? 'Sign in'
 : tab === 'register' ? 'Create account'
 : tab === 'forgot' ? 'Send reset link'
 : 'Reset password'}
 {!loading && <span className="font-serif text-[15px] leading-none">→</span>}
 </button>

 {/* Back to sign in — forgot/reset tabs */}
 {(tab === 'forgot' || tab === 'reset') && (
 <button
 type="button"
 onClick={() => { setTab('login'); setError(null); setSuccess(null); }}
 className="w-full text-center text-[11px] text-ink3 hover:text-ink transition-colors mt-4"
 >
 ← Back to sign in
 </button>
 )}
 </form>

 {/* Footer note */}
 <div className="px-8 pt-4 pb-[22px] mt-2 border-t border-rule-soft">
 <p className="font-serif italic text-[11px] text-ink3 leading-[1.5] text-center m-0">
 Your cases are saved securely, so you can resume from any device.
 </p>
 </div>
 </div>
 </div>
 );
};
